import React, { ElementType, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Tab, TabList, TabPanel, Tabs } from '../../../../../../../../CommonComponents/OPL/Tab';
import {
  ACCOUNTS_WITH_BENEFICIARIES_TAB,
  ACCOUNTS_WITH_HOLDINGS_TAB,
  AnnuityType,
  FormComponentProps,
  FormFields,
  getAccountHasAssetAllocationTab,
} from '../../AccountForm.meta';
import { Alert } from '../../../../../../../../CommonComponents/OPL/Alert/Alert';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { isFinancialPlanEnabledSelector } from '../../../../../../../../CommonSelectors/CommonAreaSelector';
import { Beneficiaries } from '../Beneficiaries/Beneficiaries';
import { HoldingsTab } from '../HoldingsTab/HoldingsTab';
import { AssetAllocation } from '../AssetAllocation/AssetAllocation';
import { SyncOptions } from '../SyncOptions/SyncOptions';
import { AccountModalUsageContext } from '../../context/AccountFormProvider.meta';

interface Tab {
  Component: ElementType;
  name: string;
}

interface Props extends FormComponentProps {
  tabs: Tab[];
}

export const TabsContent = ({
  tabs,
  defaultSelectedTab,
  accountType,
  errorMessage,
  accountId,
}: Props) => {
  const formBag = useFormContext<FormFields>();
  const {
    formState: { errors },
  } = formBag;

  const { alert, setAlert, usageContext } = useAccountFormContext();

  const [annuityType, isLinked, isIntegrated] = formBag.watch([
    'bankAccount.AnnuityType',
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
  ]);

  const isFinancialPlanEnabled = useSelector(isFinancialPlanEnabledSelector);

  const enhancedTabs = [...tabs];

  if (
    typeof accountType === 'number' &&
    ACCOUNTS_WITH_HOLDINGS_TAB.includes(accountType) &&
    annuityType !== AnnuityType.Fixed &&
    usageContext !== AccountModalUsageContext.AnnuityPurchaseWhatIf
  ) {
    enhancedTabs.push({ name: 'Holdings', Component: HoldingsTab });
  }

  if (
    isFinancialPlanEnabled &&
    getAccountHasAssetAllocationTab(accountType, { annuityType }) &&
    usageContext !== AccountModalUsageContext.AnnuityPurchaseWhatIf
  ) {
    enhancedTabs.push({ name: 'Asset Allocation', Component: AssetAllocation });
  }

  if (
    isFinancialPlanEnabled &&
    typeof accountType === 'number' &&
    ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(accountType)
  ) {
    enhancedTabs.push({ name: 'Beneficiaries', Component: Beneficiaries });
  }

  if (isLinked || isIntegrated) {
    enhancedTabs.push({
      name: 'Sync Options',
      Component: SyncOptions,
    });
  }

  const defaultSelectedTabIndex = defaultSelectedTab
    ? enhancedTabs.findIndex(({ name }) => name === defaultSelectedTab)
    : 0;

  const [selectedTabIndex, setSelectedTabIndex] = useState(defaultSelectedTabIndex);

  return (
    <Tabs selectedIndex={selectedTabIndex} onSelect={setSelectedTabIndex}>
      <TabList>
        {enhancedTabs.map(({ name }) => (
          <Tab key={name}>{name}</Tab>
        ))}
      </TabList>

      {errorMessage && <Alert mt="16" message={errorMessage} variant="error" />}

      {errors.bankAccount?.OwnerModel && errors.bankAccount?.OwnerModel[0]?.Id && (
        <Alert
          mt="16"
          message={errors.bankAccount?.OwnerModel[0]?.Id.message ?? ''}
          variant="error"
        />
      )}

      {alert && (
        <Alert
          message={alert.message}
          variant={alert.type}
          onRequestClose={() => setAlert(null)}
          scrollIntoViewOptions={{ block: 'start' }}
          mt="16"
        />
      )}

      {enhancedTabs.map(({ Component, name }) => (
        <TabPanel key={name}>
          <Component accountType={accountType} accountId={accountId} />
        </TabPanel>
      ))}
    </Tabs>
  );
};
